import { useState, useEffect, useRef } from 'react';
import { latestBooks } from '@/mocks/books';
import { getBookPublicationBadge, getBookPublicationBadgeClass, getBookPublicationBadgeLabel } from '../../../../../shared/book-publication-status';

const INITIAL_DISPLAY_COUNT = 18;

export default function NewBooks2026Section() {
  const [showAll, setShowAll] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const gridRef = useRef<HTMLDivElement>(null);

  const displayedBooks = showAll ? latestBooks : latestBooks.slice(0, INITIAL_DISPLAY_COUNT);
  const hasMore = latestBooks.length > INITIAL_DISPLAY_COUNT;

  useEffect(() => {
    const el = ref.current;
    if (!el) return;

    const mediaQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
    if (mediaQuery.matches) {
      el.querySelectorAll('[data-animate]').forEach((child) => {
        (child as HTMLElement).classList.remove('opacity-0', 'translate-y-6');
      });
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const children = entry.target.querySelectorAll('[data-animate]');
            children.forEach((child, i) => {
              setTimeout(() => {
                (child as HTMLElement).classList.remove('opacity-0', 'translate-y-6');
              }, 100 + i * 60);
            });
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.1 }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  const handleToggle = () => {
    setShowAll((prev) => {
      if (!prev && gridRef.current) {
        setTimeout(() => {
          gridRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }, 100);
      }
      return !prev;
    });
  };

  return (
    <section className="bg-background-100">
      <div ref={ref} className="section-container w-full py-16 md:py-20">
        <div className="text-center mb-10 md:mb-14 max-w-[800px] mx-auto">
          <span data-animate className="opacity-0 translate-y-6 transition-all duration-700 inline-block px-3 py-1 text-xs font-label bg-accent-50 text-accent-700 rounded-full mb-3">
            最新發行
          </span>
          <h2 data-animate className="opacity-0 translate-y-6 transition-all duration-700 text-2xl md:text-3xl font-heading font-semibold text-foreground-900 mb-4">
            2026 最新發行
          </h2>
          <p data-animate className="opacity-0 translate-y-6 transition-all duration-700 text-sm md:text-base text-foreground-500 leading-relaxed max-w-[720px] mx-auto">
            探索未境原創於 2026 年最新發行的愛情小說作品。
          </p>
        </div>

        <div ref={gridRef} data-animate className="opacity-0 translate-y-6 transition-all duration-700">
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-4 md:gap-5">
            {displayedBooks.map((book) => (
              <div
                key={book.id}
                className="group rounded-xl overflow-hidden bg-background-50 border border-background-200/70 hover:border-accent-200 transition-all cursor-pointer"
              >
                <div className="relative aspect-[2/3] overflow-hidden">
                  <img
                    src={book.coverUrl || ''}
                    alt={book.title}
                    className="w-full h-full object-contain bg-white group-hover:scale-[1.02] transition-transform duration-500"
                  />
                  {(() => {
                    const badge = getBookPublicationBadge(book.publishDate);
                    return badge ? (
                      <span className={`absolute top-2 left-2 rounded-full px-2 py-0.5 text-xs font-medium ${getBookPublicationBadgeClass(badge)}`}>
                        {getBookPublicationBadgeLabel(badge)}
                      </span>
                    ) : null;
                  })()}
                </div>
                <div className="p-3 md:p-4">
                  <div className="flex flex-wrap gap-1 mb-2">
                    {book.tags.slice(0, 2).map((tag) => (
                      <span
                        key={tag}
                        className="px-2 py-0.5 text-[10px] font-label bg-secondary-50 text-secondary-700 rounded-full whitespace-nowrap"
                      >
                        {tag}
                      </span>
                    ))}
                  </div>
                  <h3 className="text-sm md:text-[13px] font-heading font-semibold text-foreground-900 mb-1 leading-snug">
                    {book.title}
                  </h3>
                  <p className="text-xs text-foreground-500">
                    {book.author}
                  </p>
                </div>
              </div>
            ))}
          </div>

          {hasMore && (
            <div className="text-center mt-8 md:mt-10">
              <button
                onClick={handleToggle}
                className="inline-flex items-center gap-2 px-6 py-3 text-sm font-label bg-background-50 border border-background-200/70 text-foreground-700 rounded-full hover:border-accent-300 hover:text-accent-700 transition-all cursor-pointer whitespace-nowrap"
              >
                {showAll ? (
                  <>
                    收起部分作品
                    <i className="ri-arrow-up-s-line text-base"></i>
                  </>
                ) : (
                  <>
                    顯示全部 {latestBooks.length} 部作品
                    <i className="ri-arrow-down-s-line text-base"></i>
                  </>
                )}
              </button>
              {!showAll && (
                <p className="text-xs text-foreground-400 mt-2">
                  目前顯示前 {INITIAL_DISPLAY_COUNT} 部，共 {latestBooks.length} 部作品
                </p>
              )}
            </div>
          )}
        </div>

        <section data-animate className="opacity-0 translate-y-6 transition-all duration-700 mt-14 md:mt-18 pt-10 md:pt-12 border-t border-background-200/70">
          <div className="text-center max-w-[720px] mx-auto">
            <h2 className="text-lg md:text-xl font-heading font-semibold text-foreground-900 mb-3">
              查詢完整書目
            </h2>
            <p className="text-sm md:text-base text-foreground-500 leading-relaxed mb-6">
              完整書名、作者及相關出版資訊，以崧博書籍資料庫中的個別書籍頁為準。
            </p>
            <a
              href="https://catalog.sonbook.net/"
              target="_blank"
              rel="nofollow noopener noreferrer"
              className="inline-flex items-center gap-2 px-6 py-3 text-sm font-label bg-primary-500 text-white rounded-full hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
            >
              前往書籍資料庫
              <i className="ri-arrow-right-line text-xs"></i>
            </a>
          </div>
        </section>
      </div>
    </section>
  );
}
